// src/pages/tournamentDetails/EntrantsPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAt,
  endAt,
  where,
  writeBatch,
} from "firebase/firestore";

function placeSuffix(n) {
  const v = Number(n);
  if (v % 100 >= 11 && v % 100 <= 13) return "th";
  if (v % 10 === 1) return "st";
  if (v % 10 === 2) return "nd";
  if (v % 10 === 3) return "rd";
  return "th";
}

function normalizeMember(docId, data) {
  const d = data || {};
  const name = d.name || d.displayName || d.email || docId;
  const email = d.email || "";
  return {
    uid: docId,
    id: docId,
    name,
    email,
    status: d.status || "",
    role: d.role || "member",
    profilePhotoUrl: d.profilePhotoUrl || "",
    nameLower: d.nameLower || String(name || "").toLowerCase(),
    emailLower: d.emailLower || String(email || "").toLowerCase(),
  };
}

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "";
  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (first + last).toUpperCase();
}

function EntrantThumb({ name, photoUrl, sizeClass = "h-[72px] w-[72px]" }) {
  const initials = initialsFromName(name);

  if (photoUrl) {
    return (
      <div
        className={`${sizeClass} shrink-0 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900`}
      >
        <img
          src={photoUrl}
          alt={name ? `${name} profile` : "profile"}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className={`${sizeClass} shrink-0 rounded-lg border border-zinc-800 flex items-center justify-center`}
      style={{
        background:
          "linear-gradient(180deg, rgba(184,115,51,0.14) 0%, rgba(24,24,27,0.90) 100%)",
      }}
      title={name || "Entrant"}
    >
      <div
        className="h-11 w-11 rounded-full flex items-center justify-center text-sm font-bold"
        style={{
          border: "1px solid rgba(184,115,51,0.25)",
          background: "rgba(24,24,27,0.72)",
          color: "#B87333",
          boxShadow: "0 0 0 2px rgba(184,115,51,0.08)",
        }}
      >
        {initials || "—"}
      </div>
    </div>
  );
}

function SearchAvatar({ name, photoUrl }) {
  const initials = initialsFromName(name);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name ? `${name} profile` : "profile"}
        className="h-10 w-10 rounded-full object-cover shrink-0"
        style={{
          border: "1px solid rgba(184,115,51,0.22)",
          boxShadow: "0 0 0 2px rgba(184,115,51,0.08)",
        }}
      />
    );
  }

  return (
    <div
      className="h-10 w-10 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
      style={{
        border: "1px solid rgba(184,115,51,0.22)",
        background: "rgba(184,115,51,0.10)",
        color: "#B87333",
        boxShadow: "0 0 0 2px rgba(184,115,51,0.08)",
      }}
      title={name || "Entrant"}
    >
      {initials || "—"}
    </div>
  );
}

function DealerAppreciationPromptModal({
  open,
  member,
  tournament,
  formatMoney,
  adding,
  onClose,
  onChoose,
}) {
  if (!open || !member) return null;

  const cost = Math.max(0, Number(tournament?.dealerAppreciationCost || 0));
  const bonus = Math.max(
    0,
    Number(tournament?.dealerAppreciationBonusChips || 0)
  );
  const prizePoolAmount = Math.max(
    0,
    Number(tournament?.dealerAppreciationPrizePoolAmount || 0)
  );
  const dealerAmount = Math.max(
    0,
    Number(tournament?.dealerAppreciationDealerAmount || 0)
  );

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-3 py-4 sm:px-4">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="border-b border-zinc-800 px-5 py-4">
          <h3 className="text-xl font-bold text-[#B87333]">Dealer Appreciation</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Register{" "}
            <span className="text-zinc-100 font-semibold">{member?.name}</span>{" "}
            with dealer appreciation?
          </p>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">
                Optional Cost
              </div>
              <div className="text-zinc-100 font-semibold mt-1">
                {formatMoney(cost)}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">
                Bonus Chips
              </div>
              <div className="text-zinc-100 font-semibold mt-1">
                +{bonus.toLocaleString()}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">
                Prize Pool
              </div>
              <div className="text-zinc-100 font-semibold mt-1">
                {formatMoney(prizePoolAmount)}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">
                Dealers
              </div>
              <div className="text-zinc-100 font-semibold mt-1">
                {formatMoney(dealerAmount)}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-800 px-5 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={adding}
            className="px-4 py-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={() => onChoose(false)}
            disabled={adding}
            className="px-4 py-3 rounded bg-zinc-700 hover:bg-zinc-600 text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {adding ? "Adding..." : "Skip"}
          </button>

          <button
            type="button"
            onClick={() => onChoose(true)}
            disabled={adding}
            className="px-4 py-3 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {adding ? "Adding..." : "Add Dealer Appr"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EntrantsPanel({
  tournamentId,
  tournament,

  entrants,
  loadingEntrants,

  isAdmin,
  isCompleted,
  isRegistering,
  isRunning,
  showWinnerControls,

  payoutByPlace,
  formatMoney,

  onAssignSeats,
  assigningSeats,

  onRemoveEntrantWhileRegistering,
  onOpenEliminateModal,
  onDeclareWinner,

  entrantUidSet,
  onAddEntrantByAdmin,

  rebuysEnabled,
  buyIn,
  onRebuyEntrant,

  rebuysWindowOpen,
  rebuyCutoffLevel,

  addOnsEnabled,
  addOnsWindowOpen,
  addOnLevel,
  addOnChips,
  addOnCost,
  onAddOnEntrant,
}) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [dealerAppPromptOpen, setDealerAppPromptOpen] = useState(false);
  const [dealerAppBusyId, setDealerAppBusyId] = useState("");
  const blurTimerRef = useRef(null);

  const registeredSet = useMemo(() => {
    if (entrantUidSet && entrantUidSet instanceof Set) return entrantUidSet;
    const s = new Set();
    (Array.isArray(entrants) ? entrants : []).forEach((e) =>
      s.add(String(e.uid || e.id))
    );
    return s;
  }, [entrantUidSet, entrants]);

  const dealerAppreciationEnabled =
    !!tournament?.dealerAppreciationEnabled &&
    Number(tournament?.dealerAppreciationCost || 0) > 0;

  const safeClose = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  const pick = (m) => {
    setSelected(m);
    setTerm(m?.name ? `${m.name}${m.email ? ` (${m.email})` : ""}` : "");
    setOpen(false);
    setResults([]);
  };

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!isAdmin || isCompleted) return;

      const qText = String(term || "").trim().toLowerCase();
      if (qText.length < 2) {
        if (!alive) return;
        setResults([]);
        setLoadingSearch(false);
        return;
      }

      setLoadingSearch(true);

      try {
        const usersRef = collection(db, "users");

        const nameQ = query(
          usersRef,
          where("status", "==", "active"),
          orderBy("nameLower", "asc"),
          startAt(qText),
          endAt(qText + "\uf8ff"),
          limit(10)
        );

        const emailQ = query(
          usersRef,
          where("status", "==", "active"),
          orderBy("emailLower", "asc"),
          startAt(qText),
          endAt(qText + "\uf8ff"),
          limit(10)
        );

        const [nameSnap, emailSnap] = await Promise.all([
          getDocs(nameQ),
          getDocs(emailQ),
        ]);

        const merged = new Map();
        nameSnap.docs.forEach((d) =>
          merged.set(d.id, normalizeMember(d.id, d.data()))
        );
        emailSnap.docs.forEach((d) =>
          merged.set(d.id, normalizeMember(d.id, d.data()))
        );

        const out = Array.from(merged.values())
          .filter((m) => !registeredSet.has(String(m.uid)))
          .slice(0, 10);

        if (!alive) return;
        setResults(out);
      } catch (e) {
        if (!alive) return;
        console.error("Entrant search failed:", e);
        setResults([]);
      } finally {
        if (alive) setLoadingSearch(false);
      }
    };

    const t = setTimeout(run, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [term, isAdmin, isCompleted, registeredSet]);

  const canAdd = !!selected?.uid && !registeredSet.has(String(selected.uid));

  const addSelectedEntrantWithOption = async (dealerAppreciationOptIn) => {
    if (!isAdmin || isCompleted) return;
    if (!tournamentId) return alert("Missing tournament id.");
    if (!selected?.uid) return alert("Select a member first.");

    const uid = String(selected.uid);
    if (registeredSet.has(uid)) return alert("That member is already registered.");

    if (typeof onAddEntrantByAdmin !== "function") {
      return alert("Add entrant handler not wired. Check TournamentDetails props.");
    }

    setAdding(true);
    try {
      await onAddEntrantByAdmin(selected, { dealerAppreciationOptIn });
      setSelected(null);
      setTerm("");
      setResults([]);
      setOpen(false);
      setDealerAppPromptOpen(false);
    } catch (e) {
      console.error("Add entrant failed:", e);
      alert("Could not add entrant. Check permissions.");
    }
    setAdding(false);
  };

  const addSelectedEntrant = async () => {
    if (!selected?.uid) return;

    if (dealerAppreciationEnabled) {
      setDealerAppPromptOpen(true);
      return;
    }

    await addSelectedEntrantWithOption(false);
  };

  const setDealerAppreciationForEntrant = async (player, optIn) => {
    if (!isAdmin || isCompleted) return;
    if (!isRegistering) {
      return alert("Dealer Appreciation can only be changed while registering.");
    }
    if (!player?.id) return;

    const wantsOptIn = !!optIn;

    if (wantsOptIn && !dealerAppreciationEnabled) {
      return alert("Dealer Appreciation is not enabled for this tournament.");
    }

    if (!!player?.dealerAppreciationOptIn === wantsOptIn) return;

    const cost = wantsOptIn
      ? Math.max(0, Number(tournament?.dealerAppreciationCost || 0))
      : 0;
    const bonus = wantsOptIn
      ? Math.max(0, Number(tournament?.dealerAppreciationBonusChips || 0))
      : 0;
    const prizePoolAmount = wantsOptIn
      ? Math.max(
          0,
          Number(tournament?.dealerAppreciationPrizePoolAmount || 0)
        )
      : 0;
    const dealerAmount = wantsOptIn
      ? Math.max(0, Number(tournament?.dealerAppreciationDealerAmount || 0))
      : 0;

    const label =
      player?.displayName ||
      player?.name ||
      player?.email ||
      player?.uid ||
      player?.id ||
      "Player";

    const ok = window.confirm(
      wantsOptIn
        ? `Add Dealer Appreciation for ${label}?\n\nCost: ${formatMoney(
            cost
          )}\nBonus Chips: +${bonus.toLocaleString()}\nPrize Pool: ${formatMoney(
            prizePoolAmount
          )}\nDealers: ${formatMoney(dealerAmount)}`
        : `Remove Dealer Appreciation for ${label}?\n\nThis removes the Dealer Appreciation bonus chips and reverses the associated prize-pool and dealer allocations.`
    );
    if (!ok) return;

    setDealerAppBusyId(String(player.id));

    try {
      const nextEntrants = (Array.isArray(entrants) ? entrants : []).map((e) => {
        if (String(e.id) !== String(player.id)) return e;
        return {
          ...e,
          dealerAppreciationOptIn: wantsOptIn,
          dealerAppreciationCost: cost,
          dealerAppreciationBonusChips: bonus,
          dealerAppreciationPrizePoolAmount: prizePoolAmount,
          dealerAppreciationDealerAmount: dealerAmount,
        };
      });

      const daEntrants = nextEntrants.filter(
        (e) => e?.dealerAppreciationOptIn === true
      );

      const totalOptIns = daEntrants.length;
      const totalPrizePool = daEntrants.reduce(
        (sum, e) =>
          sum + Math.max(0, Number(e?.dealerAppreciationPrizePoolAmount || 0)),
        0
      );
      const totalDealerPayout = daEntrants.reduce(
        (sum, e) =>
          sum + Math.max(0, Number(e?.dealerAppreciationDealerAmount || 0)),
        0
      );

      const batch = writeBatch(db);
      const entrantRef = doc(
        db,
        "tournaments",
        tournamentId,
        "entrants",
        player.id
      );
      const tournamentRef = doc(db, "tournaments", tournamentId);

      batch.update(entrantRef, {
        dealerAppreciationOptIn: wantsOptIn,
        dealerAppreciationCost: cost,
        dealerAppreciationBonusChips: bonus,
        dealerAppreciationPrizePoolAmount: prizePoolAmount,
        dealerAppreciationDealerAmount: dealerAmount,
        ...(wantsOptIn
          ? {
              dealerAppreciationAddedByAdmin: true,
              dealerAppreciationAddedAt: serverTimestamp(),
            }
          : {
              dealerAppreciationRemovedByAdmin: true,
              dealerAppreciationRemovedAt: serverTimestamp(),
            }),
        updatedAt: serverTimestamp(),
      });

      // Keep the older aggregate fields synchronized too. The TournamentDetails
      // screen itself derives live DA totals from entrant records, but other older
      // code paths can still read these tournament-level totals.
      batch.set(
        tournamentRef,
        {
          totalDealerAppreciationOptIns: totalOptIns,
          totalDealerAppreciationPrizePool: totalPrizePool,
          totalDealerAppreciationDealerPayout: totalDealerPayout,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await batch.commit();
    } catch (e) {
      console.error("Update Dealer Appreciation failed:", e);
      alert("Could not update Dealer Appreciation. Check permissions / rules.");
    } finally {
      setDealerAppBusyId("");
    }
  };

  const canRebuy =
    !!isAdmin &&
    !isCompleted &&
    isRunning &&
    !!rebuysEnabled &&
    !!rebuysWindowOpen &&
    typeof onRebuyEntrant === "function";

  const canAddOnBase =
    !!isAdmin &&
    !isCompleted &&
    isRunning &&
    !!addOnsEnabled &&
    !!addOnsWindowOpen &&
    Number(addOnLevel || 0) > 0 &&
    Number(addOnChips || 0) > 0 &&
    typeof onAddOnEntrant === "function";

  return (
    <div className="min-w-0 bg-zinc-900 p-3 sm:p-4 rounded-lg border border-zinc-800 mb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-xl font-semibold text-[#B87333]">Entrants</h3>

        {isAdmin && !isCompleted && (
          <button
            onClick={onAssignSeats}
            disabled={assigningSeats}
            className={`px-4 py-2 rounded font-semibold ${
              assigningSeats
                ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
            }`}
          >
            {assigningSeats ? "Assigning..." : "Assign Seats"}
          </button>
        )}
      </div>

      {isAdmin && !isCompleted && (
        <div className="mb-5">
          <label className="block text-zinc-300 text-sm mb-2">
            Add entrant (search active members)
          </label>

          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative min-w-0 flex-1">
              <input
                type="text"
                className="w-full min-w-0 p-3 text-base bg-zinc-800 text-zinc-100 rounded"
                placeholder="Type name or email (min 2 chars)"
                value={term}
                onChange={(e) => {
                  setTerm(e.target.value);
                  setSelected(null);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={safeClose}
              />

              {open && String(term || "").trim().length >= 2 && (
                <div className="absolute z-50 mt-2 w-full rounded-lg border border-zinc-700 bg-zinc-950 shadow-xl overflow-hidden">
                  <div className="px-3 py-2 text-xs text-zinc-500 border-b border-zinc-800">
                    {loadingSearch ? "Searching..." : "Select a member"}
                  </div>

                  {results.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-zinc-400">
                      No matches (or already registered).
                    </div>
                  ) : (
                    <ul className="max-h-56 overflow-auto">
                      {results.map((m) => (
                        <li key={m.uid}>
                          <button
                            type="button"
                            className="w-full min-w-0 text-left px-3 py-2 hover:bg-zinc-900 text-zinc-100"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pick(m)}
                          >
                            <div className="flex items-center gap-3">
                              <SearchAvatar
                                name={m.name}
                                photoUrl={m.profilePhotoUrl}
                              />
                              <div className="min-w-0">
                                <div className="font-semibold truncate">
                                  {m.name}
                                </div>
                                {m.email ? (
                                  <div className="text-xs text-zinc-400 truncate">
                                    {m.email}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={addSelectedEntrant}
              disabled={!canAdd || adding}
              className={`px-4 py-3 rounded font-semibold ${
                canAdd && !adding
                  ? "bg-green-700 hover:bg-green-800 text-white"
                  : "bg-zinc-700 text-zinc-300 cursor-not-allowed"
              }`}
              title={canAdd ? "Add entrant" : "Select a member first"}
            >
              {adding ? "Adding..." : "Add"}
            </button>
          </div>

          {selected?.uid ? (
            <p className="text-xs text-zinc-400 mt-2">
              Selected:{" "}
              <span className="text-zinc-200 font-semibold">
                {selected.name}
              </span>
            </p>
          ) : (
            <p className="text-xs text-zinc-500 mt-2">
              Members already registered will not appear.
            </p>
          )}
        </div>
      )}

      {loadingEntrants ? (
        <p>Loading entrants...</p>
      ) : !Array.isArray(entrants) || entrants.length === 0 ? (
        <p className="text-zinc-400">No entrants yet.</p>
      ) : (
        <div className="space-y-3">
          {entrants.map((p) => {
            const label =
              p.displayName || p.name || p.email || p.uid || p.id;
            const photoUrl = String(p.profilePhotoUrl || "").trim();
            const place = Number(p.finishPlace || 0);

            const payout = place > 0 ? payoutByPlace?.get?.(place) : null;
            const showWinnings =
              !!payout &&
              Number(payout.percent || 0) > 0 &&
              Number(payout.amount || 0) > 0;

            const winningsText = showWinnings ? formatMoney(payout.amount) : "";

            const rebuyCount = Math.max(0, Number(p.rebuys || 0));
            const showRebuyBadge = rebuyCount > 0;
            const showRebuyButton = canRebuy && !!p.eliminated;

            const addOnTaken = !!p.addOnTaken;
            const showAddOnBadge = addOnTaken;
            const showAddOnButton =
              canAddOnBase && !p.eliminated && !addOnTaken;

            const earlyBirdQualified = !!p.earlyBirdQualified;
            const earlyBirdBonus = Math.max(
              0,
              Number(p.earlyBirdBonusChips || 0)
            );

            const dealerAppreciationOptIn = !!p.dealerAppreciationOptIn;
            const dealerAppreciationBonus = Math.max(
              0,
              Number(p.dealerAppreciationBonusChips || 0)
            );
            const showDealerAppreciationBadge = dealerAppreciationOptIn;
            const dealerBusy = dealerAppBusyId === String(p.id);

            const showAddDealerAppButton =
              !!isAdmin &&
              !isCompleted &&
              !!isRegistering &&
              dealerAppreciationEnabled &&
              !dealerAppreciationOptIn;

            const showRemoveDealerAppButton =
              !!isAdmin &&
              !isCompleted &&
              !!isRegistering &&
              dealerAppreciationOptIn;

            const showRemoveRegistrationButton =
              !!isAdmin && !isCompleted && !!isRegistering;

            const showEliminateButton =
              !p.eliminated &&
              !!isAdmin &&
              !isCompleted &&
              !!isRunning &&
              !showWinnerControls;

            const showDeclareWinnerButton =
              !!isAdmin &&
              !p.eliminated &&
              !isCompleted &&
              !!isRunning &&
              !!showWinnerControls;

            const actionCount = [
              showAddDealerAppButton,
              showRemoveDealerAppButton,
              showRemoveRegistrationButton,
              showRebuyButton,
              showAddOnButton,
              showEliminateButton,
              showDeclareWinnerButton,
            ].filter(Boolean).length;

            return (
              <div
                key={p.id}
                className="min-w-0 rounded-lg border border-zinc-800 bg-zinc-950 p-3 sm:p-4"
              >
                {/* On phones the actions move below the player details. */}
                <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <EntrantThumb
                      name={label}
                      photoUrl={photoUrl}
                      sizeClass="h-14 w-14 sm:h-[72px] sm:w-[72px]"
                    />

                    <div className="min-w-0 flex-1">
                      {/* Full player name: never truncate to a few characters. */}
                      <p
                        className="text-base font-semibold leading-snug text-zinc-100"
                        style={{ overflowWrap: "anywhere" }}
                      >
                        {label}
                      </p>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {isRegistering && !isCompleted && !p.eliminated && (
                          <span className="inline-flex max-w-full items-center rounded-full border border-[#B87333]/30 bg-[#B87333]/15 px-2 py-1 text-xs font-semibold leading-tight text-[#B87333]">
                            Registered
                          </span>
                        )}

                        {isRegistering && !isCompleted && !p.eliminated && earlyBirdQualified && (
                          <span className="inline-flex max-w-full items-center rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2 py-1 text-xs font-semibold leading-tight text-yellow-300">
                            {earlyBirdBonus > 0
                              ? `Early Bird +${earlyBirdBonus.toLocaleString()}`
                              : "Early Bird"}
                          </span>
                        )}

                        {showDealerAppreciationBadge && (
                          <span className="inline-flex max-w-full items-center rounded-full border border-[#B87333]/30 bg-[#B87333]/10 px-2 py-1 text-xs font-semibold leading-tight text-[#f5d0a8]">
                            {dealerAppreciationBonus > 0
                              ? `Dealer Appr'n +${dealerAppreciationBonus.toLocaleString()}`
                              : "Dealer Appr'n"}
                          </span>
                        )}

                        {showRebuyBadge && (
                          <span className="inline-flex max-w-full items-center rounded-full border border-purple-600/30 bg-purple-700/20 px-2 py-1 text-xs font-semibold leading-tight text-purple-300">
                            Rebuy x{rebuyCount}
                          </span>
                        )}

                        {showAddOnBadge && (
                          <span className="inline-flex max-w-full items-center rounded-full border border-blue-600/30 bg-blue-700/20 px-2 py-1 text-xs font-semibold leading-tight text-blue-200">
                            Add-On
                          </span>
                        )}

                        {place > 0 && (
                          <span className="text-sm font-semibold text-zinc-300">
                            {place}{placeSuffix(place)} Place
                          </span>
                        )}

                        {p.winner && (
                          <span className="text-sm font-semibold text-green-400">
                            🏆 Winner
                          </span>
                        )}

                        {showWinnings && (
                          <span className="text-sm font-bold text-[#B87333]">
                            {winningsText}
                          </span>
                        )}

                        {!isCompleted &&
                        !p.eliminated &&
                        p.tableNumber &&
                        p.seatNumber ? (
                          <span className="text-sm font-normal text-zinc-300">
                            Table {p.tableNumber}, {p.seatLabel || `Seat ${p.seatNumber}`}
                          </span>
                        ) : null}
                      </div>

                      {isCompleted ? (
                        <p className="mt-2 text-sm leading-relaxed text-zinc-400" style={{ overflowWrap: "anywhere" }}>
                          {place > 0 ? (
                            <>
                              Finished:{" "}
                              <span className="font-semibold text-zinc-200">
                                {place}{placeSuffix(place)}
                              </span>
                              {showWinnings ? (
                                <>
                                  {" "}•{" "}
                                  <span className="font-semibold text-[#B87333]">
                                    {winningsText}
                                  </span>
                                </>
                              ) : null}
                            </>
                          ) : (
                            <span className="text-zinc-500">No finish recorded</span>
                          )}
                          {p.eliminatedByName && place !== 1 ? (
                            <>
                              {" "}— Eliminated by{" "}
                              <span className="font-semibold text-zinc-200">
                                {p.eliminatedByName}
                              </span>
                            </>
                          ) : null}
                        </p>
                      ) : p.eliminated ? (
                        <p className="mt-2 text-sm leading-relaxed text-zinc-400" style={{ overflowWrap: "anywhere" }}>
                          Eliminated — Finish:{" "}
                          <span className="font-semibold text-zinc-200">
                            {place ? `${place}${placeSuffix(place)}` : "—"}
                          </span>
                          {showWinnings ? (
                            <>
                              {" "}•{" "}
                              <span className="font-semibold text-[#B87333]">
                                {winningsText}
                              </span>
                            </>
                          ) : null}
                          {p.eliminatedByName ? (
                            <>
                              {" "}— Eliminated by{" "}
                              <span className="font-semibold text-zinc-200">
                                {p.eliminatedByName}
                              </span>
                            </>
                          ) : null}
                          {isRunning && rebuysEnabled ? (
                            <span className="mt-1 block text-xs text-zinc-500">
                              {rebuysWindowOpen ? (
                                <>Rebuy available: {formatMoney(buyIn)}</>
                              ) : (
                                <>
                                  Rebuy period ended
                                  {rebuyCutoffLevel
                                    ? ` (end of Level ${rebuyCutoffLevel})`
                                    : ""}
                                </>
                              )}
                            </span>
                          ) : null}
                        </p>
                      ) : isRunning ? (
                        <div className="mt-2 space-y-1 text-sm text-zinc-400">
                          <div>Still in</div>
                          {addOnsEnabled ? (
                            <div className="text-xs leading-relaxed text-zinc-500">
                              {addOnsWindowOpen ? (
                                addOnTaken ? (
                                  <>Add-on taken</>
                                ) : (
                                  <>
                                    Add-on available: +
                                    {Number(addOnChips || 0).toLocaleString()} chips
                                    for {formatMoney(addOnCost || 0)}
                                  </>
                                )
                              ) : (
                                <>
                                  Add-on unlocks after end of Level{" "}
                                  <span className="font-semibold text-zinc-200">
                                    {addOnLevel || "—"}
                                  </span>
                                </>
                              )}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Buttons do not take away width from names on mobile. */}
                  {actionCount > 0 && (
                    <div
                      className={`grid min-w-0 w-full gap-2 border-t border-zinc-800 pt-3 lg:w-auto lg:shrink-0 lg:grid-cols-1 lg:border-t-0 lg:pt-0 ${
                        actionCount === 1 ? "grid-cols-1" : "grid-cols-2"
                      }`}
                    >
                      {showAddDealerAppButton && (
                        <button
                          type="button"
                          onClick={() => setDealerAppreciationForEntrant(p, true)}
                          disabled={dealerBusy}
                          className={`min-w-0 rounded-md px-3 py-3 text-sm font-semibold leading-snug lg:min-w-[160px] ${
                            dealerBusy
                              ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                              : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
                          }`}
                          title="Add Dealer Appreciation to this entrant"
                        >
                          {dealerBusy ? "Saving..." : "Add Dealer Appr"}
                        </button>
                      )}

                      {showRemoveDealerAppButton && (
                        <button
                          type="button"
                          onClick={() => setDealerAppreciationForEntrant(p, false)}
                          disabled={dealerBusy}
                          className={`min-w-0 rounded-md border px-3 py-3 text-sm font-semibold leading-snug lg:min-w-[160px] ${
                            dealerBusy
                              ? "bg-zinc-800 border-zinc-700 text-zinc-400 cursor-not-allowed"
                              : "bg-zinc-700 hover:bg-zinc-600 border-zinc-600 text-zinc-100"
                          }`}
                          title="Remove Dealer Appreciation from this entrant"
                        >
                          {dealerBusy ? "Saving..." : "Remove Dealer Appr"}
                        </button>
                      )}

                      {showRemoveRegistrationButton && (
                        <button
                          type="button"
                          onClick={() => onRemoveEntrantWhileRegistering(p)}
                          className="min-w-0 rounded-md bg-red-600 px-3 py-3 text-sm font-semibold leading-snug text-white hover:bg-red-700 lg:min-w-[160px]"
                        >
                          Remove
                        </button>
                      )}

                      {showRebuyButton && (
                        <button
                          type="button"
                          onClick={() => onRebuyEntrant?.(p)}
                          className="min-w-0 rounded-md bg-purple-700 px-3 py-3 text-sm font-semibold leading-snug text-white hover:bg-purple-800 lg:min-w-[160px]"
                          title={`Rebuy adds ${formatMoney(buyIn)} to prize pool`}
                        >
                          Rebuy
                        </button>
                      )}

                      {showAddOnButton && (
                        <button
                          type="button"
                          onClick={() => onAddOnEntrant?.(p)}
                          className="min-w-0 rounded-md bg-blue-700 px-3 py-3 text-sm font-semibold leading-snug text-white hover:bg-blue-800 lg:min-w-[160px]"
                          title={`Add-on: +${Number(
                            addOnChips || 0
                          ).toLocaleString()} chips for ${formatMoney(
                            addOnCost || 0
                          )}`}
                        >
                          Add-On
                        </button>
                      )}

                      {showEliminateButton && (
                        <button
                          type="button"
                          onClick={() => onOpenEliminateModal(p)}
                          className="min-w-0 rounded-md bg-red-600 px-3 py-3 text-sm font-semibold leading-snug text-white hover:bg-red-700 lg:min-w-[160px]"
                        >
                          Eliminate
                        </button>
                      )}

                      {showDeclareWinnerButton && (
                        <button
                          type="button"
                          onClick={() => onDeclareWinner(p)}
                          className="min-w-0 rounded-md bg-green-700 px-3 py-3 text-sm font-semibold leading-snug text-white hover:bg-green-800 lg:min-w-[160px]"
                        >
                          Declare Winner
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DealerAppreciationPromptModal
        open={dealerAppPromptOpen}
        member={selected}
        tournament={tournament}
        formatMoney={formatMoney}
        adding={adding}
        onClose={() => {
          if (!adding) setDealerAppPromptOpen(false);
        }}
        onChoose={addSelectedEntrantWithOption}
      />
    </div>
  );
}
